import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { saveAs } from "file-saver";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";
import { db, doc, updateDoc, arrayRemove, storage, ref, uploadBytes, getDownloadURL } from "../../../../firebaseConfig";
import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";

// Utility function to calculate GCD (Greatest Common Divisor)
const gcd = (a, b) => {
  return b === 0 ? a : gcd(b, a % b);
};

// Utility function to calculate and format aspect ratio
const calculateAspectRatio = (width, height) => {
  if (!width || !height) return "";

  const divisor = gcd(width, height);
  const ratioW = width / divisor;
  const ratioH = height / divisor;

  // Define standard aspect ratios with both orientations
  const standardRatios = [
    { w: 1, h: 1, name: "1:1" },
    { w: 3, h: 2, name: "3:2" },
    { w: 2, h: 3, name: "2:3" },
    { w: 4, h: 3, name: "4:3" },
    { w: 3, h: 4, name: "3:4" },
    { w: 16, h: 9, name: "16:9" },
    { w: 9, h: 16, name: "9:16" },
    { w: 5, h: 4, name: "5:4" },
    { w: 4, h: 5, name: "4:5" },
    { w: 5, h: 3, name: "5:3" },
    { w: 3, h: 5, name: "3:5" },
  ];

  // Check if the calculated ratio matches a standard (within 2% tolerance)
  const calculatedRatio = width / height;
  const tolerance = 0.02; // 2% tolerance

  for (const standard of standardRatios) {
    const standardRatio = standard.w / standard.h;
    const difference = Math.abs(calculatedRatio - standardRatio) / standardRatio;

    if (difference <= tolerance) {
      return standard.name;
    }
  }

  // If no standard ratio matches, return the simplified ratio
  return `${ratioW}:${ratioH}`;
};

const FileSizesV2 = ({
  client,
  updateFS,
  filter = false,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  
  const [lifeFileSizes, setLifeFileSizes] = useState(client?.fileSizes?.life);
  const [productFileSizes, setProductFileSizes] = useState(
    client?.fileSizes?.product
  );
  const [videoFileSizes, setVideoFileSizes] = useState(
    client?.fileSizes?.video
  );

  useEffect(() => {
    setLifeFileSizes(client?.fileSizes?.life);
    setProductFileSizes(client?.fileSizes?.product);
    setVideoFileSizes(client?.fileSizes?.video);
  }, [client]);

  const categories = [];

  if (!filter || filter.toLowerCase() === "life") {
    categories.push(
      <FileSizeCategoryV2
        key="FS-CAT-LIFE"
        kind="Life"
        fileSizes={lifeFileSizes}
        client={client}
        topMargin={categories.length > 0}
        canEdit={isAdmin}
        updateFS={updateFS}
        theme={theme}
      />
    );
  }

  if (!filter || filter.toLowerCase() === "product") {
    categories.push(
      <FileSizeCategoryV2
        key="FS-CAT-PRODUCT"
        kind="Product"
        fileSizes={productFileSizes}
        client={client}
        topMargin={categories.length > 0}
        canEdit={isAdmin}
        updateFS={updateFS}
        theme={theme}
      />
    );
  }

  if (!filter || filter.toLowerCase() === "video") {
    categories.push(
      <FileSizeCategoryV2
        key="FS-CAT-VIDEO"
        kind="Video"
        fileSizes={videoFileSizes}
        client={client}
        topMargin={categories.length > 0}
        canEdit={isAdmin}
        updateFS={updateFS}
        theme={theme}
      />
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {categories}
    </Box>
  );
};

const FileSizeCategoryV2 = ({
  kind,
  fileSizes,
  client,
  topMargin,
  canEdit,
  updateFS,
  theme,
}) => {
  const [showDeleteSupplySize, setShowDeleteSupplySize] = useState(false);
  const [openReorderDialog, setOpenReorderDialog] = useState(false);
  const [reorderedFileSizes, setReorderedFileSizes] = useState(fileSizes || []);

  useEffect(() => {
    setReorderedFileSizes(fileSizes || []);
  }, [fileSizes]);

  let deleteSupplySizeTimeout = null;

  const handleNewSupplySizeClick = () => {
    if (!canEdit) return;
    if (!client.fileSizes) client.fileSizes = {};
    if (!client.fileSizes[kind.toLowerCase()]) client.fileSizes[kind.toLowerCase()] = [];
    
    client.fileSizes[`${kind.toLowerCase()}`].push({
      title: "",
      width: "",
      height: "",
      dpi: "",
      backgroundColour: "",
      fileSize: "",
      fileSizeUnit: "",
      fileFormat: "",
      colourSpace: "",
      noSupply: false,
    });
    updateFS(client.id, "fileSizes", client.fileSizes);
  };

  const handleDeleteSupplySizeClick = () => {
    if (!canEdit) return;
    if (deleteSupplySizeTimeout) {
      clearTimeout(deleteSupplySizeTimeout);
      deleteSupplySizeTimeout = null;
    } else {
      deleteSupplySizeTimeout = setTimeout(() => {
        setShowDeleteSupplySize(false);
        deleteSupplySizeTimeout = null;
      }, 15000);
    }
    setShowDeleteSupplySize(!showDeleteSupplySize);
  };

  const handleReorderClick = () => {
    setOpenReorderDialog(true);
  };

  const handleCloseReorderDialog = () => {
    setOpenReorderDialog(false);
  };

  const handleSaveReorderedFileSizes = () => {
    if (!canEdit) return;
    client.fileSizes[kind.toLowerCase()] = reorderedFileSizes;
    updateFS(client.id, "fileSizes", client.fileSizes);
    setOpenReorderDialog(false);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(reorderedFileSizes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setReorderedFileSizes(items);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  return (
    <FormControl
      fullWidth
      sx={{
        position: "relative",
        mb: 3,
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {kind}
            </Typography>
            <Box sx={{ display: canEdit ? "flex" : "none" }}>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton 
                  onClick={handleReorderClick}
                  sx={{
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <SwapVertIcon />
                </IconButton>
              </motion.div>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton 
                  onClick={handleNewSupplySizeClick}
                  sx={{
                    ml: 1,
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </motion.div>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={handleDeleteSupplySizeClick}
                  sx={{
                    ml: 1,
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Box>

          <Dialog open={openReorderDialog} onClose={handleCloseReorderDialog}>
            <DialogTitle>Reorder {kind} File Sizes</DialogTitle>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="fileSizesList">
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {reorderedFileSizes.map((fs, index) => (
                      <Draggable key={index} draggableId={`${index}`} index={index}>
                        {(provided) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              background: fs.noSupply
                                ? theme === "dark"
                                  ? "rgba(240, 100, 100, 0.04)"
                                  : "rgba(240, 100, 100, 0.08)"
                                : theme === "dark"
                                ? "#363636"
                                : "#f8f8f8",
                              borderBottom: "1px solid grey",
                              marginBottom: 1,
                            }}
                          >
                            <DragHandleIcon sx={{ marginRight: 2 }} />
                            <ListItemText primary={fs.title || "Untitled"} />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
            <DialogActions>
              <Button onClick={handleCloseReorderDialog}>Cancel</Button>
              <Button onClick={handleSaveReorderedFileSizes}>Save</Button>
            </DialogActions>
          </Dialog>

          <Grid container spacing={2} justifyContent="center">
            {fileSizes &&
              fileSizes.map((fs, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`FileSizeContainer-${index}`}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <FileSizeContainerV2
                    fs={fs}
                    client={client}
                    showDeleteSupplySize={showDeleteSupplySize}
                    kind={kind}
                    updateFS={updateFS}
                    canEdit={canEdit}
                    theme={theme}
                  />
                </Grid>
              ))}
          </Grid>
          
          {(!fileSizes || fileSizes.length === 0) && (
            <Box
              sx={{
                textAlign: "center",
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.5)"
                    : "rgba(0,0,0,0.5)",
                py: 4,
                fontStyle: "italic",
              }}
            >
              No {kind.toLowerCase()} file sizes yet. Click the + button to add one.
            </Box>
          )}
        </motion.div>
      </Box>
    </FormControl>
  );
};

const FileSizeContainerV2 = ({
  fs,
  client,
  showDeleteSupplySize,
  kind,
  updateFS,
  canEdit,
  theme,
}) => {
  const fileFormats = ["psd", "jpg", "png", "webp", "tiff", "mp4", "webm"];
  const colourSpaces = ["sRGB", "Adobe 1998", "CMYK"];
  const fileSizeUnits = ["KB", "MB"];

  const fileUploadRef = useRef(null);

  const [title, setTitle] = useState(fs.title);
  const [width, setWidth] = useState(fs.width);
  const [height, setHeight] = useState(fs.height);
  const [dpi, setDpi] = useState(fs.dpi);
  const [fileFormat, setFileFormat] = useState(fs.fileFormat);
  const [removeITSBackground, setRemoveITSBackground] = useState(
    fs.removeITSBackground
  );
  const [backgroundColour, setBackgroundColour] = useState(fs.backgroundColour);
  const [colourSpace, setColourSpace] = useState(fs.colourSpace);
  const [fileSize, setFileSize] = useState(fs.fileSize);
  const [fileSizeUnit, setFileSizeUnit] = useState(fs.fileSizeUnit);
  const [noSupply, setNoSupply] = useState(fs.noSupply || false);

  const [fileUploading, setFileUploading] = useState(false);

  useEffect(() => {
    setTitle(fs.title);
    setWidth(fs.width);
    setHeight(fs.height);
    setDpi(fs.dpi);
    setFileFormat(fs.fileFormat);
    setBackgroundColour(fs.backgroundColour);
    setRemoveITSBackground(fs.removeITSBackground);
    setColourSpace(fs.colourSpace);
    setFileSize(fs.fileSize);
    setFileSizeUnit(fs.fileSizeUnit);
    setNoSupply(fs.noSupply || false);
  }, [fs]);

  
  const handleFileFormatChange = (e, newValue) => {
    if (!canEdit) return;
    if (!newValue) {
      newValue = "";
    }
    setFileFormat(newValue);
    fs.fileFormat = newValue;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleRemoveITSBackgroundChange = (e) => {
    if (!canEdit) return;
    setRemoveITSBackground(e.target.checked);
    fs.removeITSBackground = e.target.checked;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleBackgroundColourChange = (e) => {
    if (!canEdit) return;
    setBackgroundColour(e.target.value);
    fs.backgroundColour = e.target.value;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleBackgroundColourChangeFileUpload = (url) => {
    if (!canEdit) return;
    setBackgroundColour(url);
    fs.backgroundColour = url;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleColourSpaceChange = (e, newValue) => {
    if (!canEdit) return;
    if (!newValue) {
      newValue = "";
    }
    setColourSpace(newValue);
    fs.colourSpace = newValue;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleFileSizeChange = (e) => {
    if (!canEdit) return;
    let val = parseInt(e.target.value);
    if (!val) {
      val = "";
    }
    setFileSize(val);
    fs.fileSize = val;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleFileSizeUnitChange = (e, newValue) => {
    if (!canEdit) return;
    if (!newValue) {
      newValue = "";
    }
    setFileSizeUnit(newValue);
    fs.fileSizeUnit = newValue;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };
  
  const handleDeleteSupplySizeClick = () => {
    if (!canEdit) return;
    updateDoc(doc(db, "ClientDatabase", client.id), {
        [`fileSizes.${kind.toLowerCase()}`]: arrayRemove(fs),
      });
  };
  
  const handleNoSupplyChange = (e) => {
    if (!canEdit) return;
    setNoSupply(e.target.checked);
    fs.noSupply = e.target.checked;
    updateFS(client.id, "fileSizes", client.fileSizes);
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "8px",
        padding: 1,
        maxWidth: 320,
        width: "100%",
        backgroundColor: noSupply
          ? theme === "dark"
            ? "rgba(240, 100, 100, 0.04)"
            : "rgba(240, 100, 100, 0.08)"
          : theme === "dark"
            ? "rgba(45,45,45,0.5)"
            : "rgba(250,250,250,0.5)",
        boxShadow: theme === "dark" ? "0 2px 5px rgba(0,0,0,0.2)" : "0 2px 5px rgba(0,0,0,0.05)",
      }}
    >
      {showDeleteSupplySize && (
        <Box
          sx={{
            background: "rgba(0,0,0,0.15)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
            position: "absolute",
            borderRadius: "8px",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <IconButton onClick={handleDeleteSupplySizeClick}>
            <DeleteIcon
              sx={{
                color: "#ff0033",
                width: 60,
                height: 60,
                filter: "drop-shadow(4px 4px 3px rgba(0,0,0,0.6))",
              }}
            />
          </IconButton>
        </Box>
      )}
      
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <UniversalTextField
            value={title || ""}
            label="Folder Name"
            variant="filled"
            disabled={!canEdit}
            onCallback={(newValue) => {
              if (!canEdit) return;
              setTitle(newValue);
              fs.title = newValue;
              updateFS(client.id, "fileSizes", client.fileSizes);
            }}
            inputProps={{ style: { fontSize: "0.8rem" } }}
            noPadding
            sx={{
              marginTop: 0,
              "& .MuiInputBase-root": {
                fontSize: "0.8rem",
              },
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <UniversalTextField
            value={width || ""}
            label="Width"
            variant="filled"
            disabled={!canEdit}
            type="number"
            onCallback={(newValue) => {
              if (!canEdit) return;
              let val = parseInt(newValue);
              if (!val) val = "";
              setWidth(val);
              fs.width = val;
              updateFS(client.id, "fileSizes", client.fileSizes);
            }}
            noPadding
            sx={{
              marginTop: 0,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <UniversalTextField
            value={height || ""}
            label="Height"
            variant="filled"
            disabled={!canEdit}
            type="number"
            onCallback={(newValue) => {
              if (!canEdit) return;
              let val = parseInt(newValue);
              if (!val) val = "";
              setHeight(val);
              fs.height = val;
              updateFS(client.id, "fileSizes", client.fileSizes);
            }}
            noPadding
            sx={{
              marginTop: 0,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            InputProps={{ disableUnderline: true }}
            fullWidth
            variant="filled"
            label="Aspect Ratio"
            value={calculateAspectRatio(width, height)}
            disabled
            sx={{
              backgroundColor: theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
              borderRadius: "8px"
            }}
          />
        </Grid>
        {kind !== "Video" && (
          <Grid item xs={6}>
            <UniversalTextField
              value={dpi || ""}
              label="DPI"
              variant="filled"
              disabled={!canEdit}
              type="number"
              onCallback={(newValue) => {
                if (!canEdit) return;
                let val = parseInt(newValue);
                if (!val) val = "";
                setDpi(val);
                fs.dpi = val;
                updateFS(client.id, "fileSizes", client.fileSizes);
              }}
              noPadding
              sx={{
                marginTop: 0,
              }}
            />
          </Grid>
        )}
        <Grid item xs={kind !== "Video" ? 6 : 12}>
          <Autocomplete
            value={fileFormat}
            onChange={handleFileFormatChange}
            inputValue={fileFormat}
            onInputChange={handleFileFormatChange}
            options={fileFormats}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                }}
                variant="filled"
                label="Format"
                disabled={!canEdit}
                sx={{
                  backgroundColor: theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                  borderRadius: "8px"
                }}
              />
            )}
          />
        </Grid>
        {kind !== "Video" && (
          <Grid item xs={12}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removeITSBackground}
                    onChange={handleRemoveITSBackgroundChange}
                    disabled={!canEdit}
                  />
                }
                label={
                  <span style={{ fontSize: 13 }}>Remove ITS Background</span>
                }
              />
            </FormGroup>
          </Grid>
        )}
        {kind !== "Video" && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="filled"
              label="Background"
              value={
                typeof backgroundColour === "object"
                  ? backgroundColour.fileName
                  : backgroundColour
              }
              onChange={handleBackgroundColourChange}
              disabled={!canEdit}
              InputProps={{
                disableUnderline: true,
                endAdornment: (
                  <>
                    {!fileUploading ? (
                      <IconButton
                        onClick={async () => {
                          if (!canEdit) return;
                          if (fileUploading) return;
                          fileUploadRef.current.value = null;
                          fileUploadRef.current.click();
                        }}
                      >
                        <FileUploadIcon />
                      </IconButton>
                    ) : (
                      <IconButton disabled>
                        <CircularProgress size={24} />
                      </IconButton>
                    )}
                    {typeof backgroundColour === "object" && (
                      <IconButton
                        onClick={() => {
                          saveAs(backgroundColour.url, backgroundColour.fileName);
                        }}
                      >
                        <FileDownloadIcon />
                      </IconButton>
                    )}
                  </>
                ),
              }}
              sx={{
                backgroundColor: theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                borderRadius: "8px"
              }}
            />
          </Grid>
        )}
        {kind !== "Video" && (
          <Grid item xs={12}>
            <Autocomplete
              value={colourSpace}
              onChange={handleColourSpaceChange}
              inputValue={colourSpace}
              onInputChange={handleColourSpaceChange}
              options={colourSpaces}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                  variant="filled"
                  label="Colour Space"
                  disabled={!canEdit}
                  sx={{
                    backgroundColor: theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                    borderRadius: "8px"
                  }}
                />
              )}
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <TextField
            fullWidth
            variant="filled"
            InputProps={{ disableUnderline: true }}
            label="Compress"
            value={fileSize}
            onChange={handleFileSizeChange}
            disabled={!canEdit}
            sx={{
              backgroundColor: theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
              borderRadius: "8px"
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            value={fileSizeUnit}
            onChange={handleFileSizeUnitChange}
            inputValue={fileSizeUnit}
            onInputChange={handleFileSizeUnitChange}
            options={fileSizeUnits}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                }}
                variant="filled"
                label="Unit"
                disabled={!canEdit}
                sx={{
                  backgroundColor: theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                  borderRadius: "8px"
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noSupply}
                  onChange={handleNoSupplyChange}
                  disabled={!canEdit}
                />
              }
              label="No Supply"
            />
          </FormGroup>
        </Grid>
      </Grid>
      
      <input
        ref={fileUploadRef}
        type="file"
        style={{ display: "none" }}
        accept="image/png, image/jpeg"
        onChange={async (e) => {
          setFileUploading(true);
          try {
            const file = e.target.files[0];
            const storageRef = ref(storage, `/Client Background Plates/${client.brand}/${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            handleBackgroundColourChangeFileUpload({
              fileName: file.name,
              url: url,
            });
            setFileUploading(false);
          } catch (e) {
            console.log(e);
            setFileUploading(false);
          }
        }}
      />
    </Box>
  );
};

export default FileSizesV2;